<template>
	<v-theme-provider :dark="effectiveAppearance === 'dark'" :light="effectiveAppearance === 'light'">
	<main class="payment-page" :class="brandClasses" :style="brandStyle">
		<section v-if="!isReady" class="payment-loading" role="status">
			<v-progress-circular indeterminate :size="34" :width="3" color="primary"></v-progress-circular>
			<p>{{ $t('invoices.payment.loading') }}</p>
		</section>

		<div v-else class="payment-shell">
			<header class="payment-brand">
				<img v-if="logo" :src="logo" alt="Logo" />
			</header>

			<section class="invoice-summary" aria-labelledby="payment-heading">
				<div class="summary-heading">
					<span class="invoice-kicker">
						{{ $t('invoices.list.invoice-number') }} {{ paymentInfo.invoiceNumberFormatted }}
					</span>
					<h1 id="payment-heading">{{ $t('invoices.payment.options') }}</h1>
				</div>

				<div class="amount-summary">
					<span>{{ $t('invoice.details.total-due') }}</span>
					<strong>
						{{ $formatters.dollars(paymentInfo.invoiceAmount, true, true, paymentInfo.currency) }}
					</strong>
				</div>

				<dl class="invoice-facts">
					<div>
						<dt>{{ $t('invoices.list.invoice-number') }}</dt>
						<dd>{{ paymentInfo.invoiceNumberFormatted }}</dd>
					</div>
					<div>
						<dt>{{ $t('invoice.details.issued') }}</dt>
						<dd>{{ DateTime.fromISO(paymentInfo.dateSent).toLocaleString(DateTime.DATE_MED) }}</dd>
					</div>
					<div>
						<dt>{{ $t('invoice.details.due') }}</dt>
						<dd>{{ DateTime.fromISO(paymentInfo.dateDue).toLocaleString(DateTime.DATE_MED) }}</dd>
					</div>
				</dl>
			</section>

			<v-alert
				v-if="paymentInfo.stripeCardFeeAmount > 0 && showPaymentForm"
				dense
				text
				color="info"
				class="payment-fee-notice"
			>
				{{
					$t('invoices.payment.card-fee-notice', {
						fee: $formatters.dollars(
							paymentInfo.stripeCardFeeAmount,
							true,
							true,
							paymentInfo.currency
						),
					})
				}}
			</v-alert>

			<section
				v-show="!showPaymentForm"
				class="payment-option-grid"
				:class="`option-count-${paymentOptionCount}`"
			>
				<article v-if="achEnabled || paymentMethods.length" class="payment-option-card">
					<header class="option-heading">
						<span class="option-icon" aria-hidden="true">
							<portal-icon name="credit-card" :size="20"></portal-icon>
						</span>
						<h2>{{ $t('invoices.payment.ach') }}</h2>
						<button
							type="button"
							class="option-action"
							:aria-label="$t('invoices.payment.ach')"
							@click="editSavedPaymentMethods"
						>
							<portal-icon name="settings" :size="18"></portal-icon>
						</button>
					</header>

					<div v-if="paymentMethods.length" class="saved-method-list">
						<button
							v-for="account in paymentMethods"
							:key="account.id"
							type="button"
							class="saved-method"
							@click="confirmPayment(account)"
						>
							<span class="saved-method-icon" aria-hidden="true">
								<portal-icon :name="paymentMethodIcon(account)" :size="19"></portal-icon>
							</span>
							<span class="saved-method-details">
								<strong>{{ account.label }}</strong>
								<span>•••• {{ account.last4 }}</span>
							</span>
							<span class="saved-method-action">
								{{ $t('invoices.payment.pay-with') }}
								<portal-icon name="arrow-right" :size="17"></portal-icon>
							</span>
						</button>
					</div>

					<p v-else class="empty-methods">{{ $t('invoices.payment.no-linked-accounts') }}</p>

					<button v-if="achEnabled" type="button" class="secondary-payment-action" @click="initializeACHLink">
						<portal-icon name="plus" :size="17"></portal-icon>
						{{ $t('invoices.payment.new-linked') }}
					</button>

					<div class="payment-provider">
						<img src="/images/invoices/stripe-logo.svg" alt="" />
						<span>{{ $t('invoices.payment.stripe') }}</span>
					</div>
				</article>

				<article v-if="cardEnabled" class="payment-option-card">
					<header class="option-heading">
						<span class="option-icon" aria-hidden="true">
							<portal-icon name="credit-card" :size="20"></portal-icon>
						</span>
						<h2>{{ $t('invoices.payment.payment-options') }}</h2>
					</header>

					<div class="card-brand-row" aria-hidden="true">
						<payment-icon size="34" name="amex"></payment-icon>
						<payment-icon size="34" name="master_card"></payment-icon>
						<payment-icon size="34" name="visa"></payment-icon>
						<payment-icon size="34" name="discover"></payment-icon>
					</div>

					<div class="wallet-options">
						<span>{{ $t('invoices.payment.apple-pay') }}</span>
						<span>{{ $t('invoices.payment.google-pay') }}</span>
					</div>

					<button type="button" class="primary-payment-action" @click="showInlinePayment">
						{{ $t('invoice.pay-now') }}
						<portal-icon name="arrow-right" :size="18"></portal-icon>
					</button>

					<div class="payment-provider">
						<img src="/images/invoices/stripe-logo.svg" alt="" />
						<span>{{ $t('invoices.payment.stripe') }}</span>
					</div>
				</article>

				<article v-show="payPalEnabled" class="payment-option-card">
					<header class="option-heading">
						<span class="option-icon paypal-icon" aria-hidden="true">P</span>
						<h2>PayPal</h2>
					</header>
					<div id="paypal-button-container" class="paypal-button-container"></div>
				</article>
			</section>

			<section v-if="showPaymentForm" class="payment-form-card">
				<header class="payment-form-heading">
					<button
						type="button"
						class="back-action"
						:aria-label="$t('invoices.payment.options')"
						@click="destroyPaymentElement"
					>
						<portal-icon name="arrow-left" :size="18"></portal-icon>
					</button>
					<div class="payment-provider payment-provider-top">
						<img src="/images/invoices/stripe-logo.svg" alt="" />
						<span>{{ $t('invoices.payment.stripe') }}</span>
					</div>
				</header>

				<div ref="paymentElement"></div>

				<v-btn
					class="primary-payment-action stripe-submit"
					:loading="paymentProcessing"
					:disabled="paymentProcessing"
					@click="submitPayment"
				>
					{{ $t('invoice.pay-now') }}
				</v-btn>
			</section>
		</div>
	</main>
	</v-theme-provider>
</template>

<script>
	import qs from 'qs';
	import HttpClient from '@/services/HttpClient';
	import PaymentIcon from '@/modules/invoices/InvoiceRenderer/PaymentIcon';
	import PortalIcon from '@/components/PortalIcon.vue';
	import { formBrandClasses, formBrandStyle, formLogo } from '@moxie/branding';
	import { portalBrandStyle, resolveDocumentBranding } from '@/utils/portalBranding';
	import { readableTextColor } from '@/utils/colorContrast';
	import ConfirmModal from '@/components/ConfirmModal';
	import { loadScript } from '@paypal/paypal-js';
	import { DateTime } from 'luxon';

	export default {
		name: 'Payment',

		props: [],

		components: { PaymentIcon, PortalIcon },

		data: function() {
			return {
				DateTime: DateTime,
				httpClient: null,
				token: null,
				paymentInfo: null,
				paypalConnection: null,
				isReady: false,
				paymentIntent: null,
				stripe: null,
				stripeElements: null,
				paymentElement: null,
				showPaymentForm: false,
				paymentProcessing: false,
				convenienceFee: 0,
				paymentIntentId: null,
				baseAmount: 0,
				paymentIntentHasCardFee: false,
				capturedBranding: null,
			};
		},

		mounted() {
			let queryString = qs.parse(self.location.search.slice(1));
			let request = JSON.parse(atob(queryString.token.split('.')[0]));

			let podUrl = request.podUrl;

			this.httpClient = new HttpClient(podUrl, false);
			this.token = queryString.token;

			this.paymentIntent = queryString.payment_intent;
			this.initialize();

			this.$vuetify.theme.dark = false;
		},

		beforeDestroy() {
			this.destroyPaymentElement();
			this.$store.commit('setDocumentBranding', null);
			this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
		},

		methods: {
			activateDocumentBranding() {
				this.$store.commit('setDocumentBranding', this.capturedBranding || false);
				this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
			},
			paymentMethodIcon(account) {
				return account && account.type === 'card' ? 'credit-card' : 'landmark';
			},

			initialize: function() {
				this.getPaymentMethods()
					.then((res) => {
						this.paymentInfo = res.data;
						this.capturedBranding = this.paymentInfo.branding;
						this.activateDocumentBranding();
						this.paypalConnection = this.paymentInfo.payPalConnection;

						if (this.paymentIntent) {
							this.$store.commit('startLoading');
							this.finalizePaymentIntent()
								.then((res) => {
									setTimeout(() => {
										window.location.href = res.data;
									}, 1000);
								})
								.catch((err) => {
									this.$store.commit('stopLoading');
									if (err.response.status === 418) {
										this.handleSuccess(err.response.data.message, this.paymentInfo.viewOnlineUrl);
									} else {
										this.handleError(err.response.data.message);
									}
								});
						} else {
							if (this.paymentInfo.invoiceAmount === 0) {
								this.handleError(
									'This invoice does not currently require payment',
									this.paymentInfo.returnUrl,
									true
								);
								return;
							}

							this.isReady = true;

							if (
								!this.paymentIntent &&
								this.paymentInfo.cardPaymentsEnabled &&
								this.paymentInfo.paymentMethods.length === 0 &&
								!this.paymentInfo.achPaymentsEnabled &&
								!this.paymentInfo.payPalPaymentsEnabled
							) {
								this.$nextTick(() => this.showInlinePayment());
							} else {
								if (this.paymentInfo.payPalPaymentsEnabled) {
									this.$nextTick(() => this.initializePayPal());
								}
							}
						}
					})
					.catch((err) => {
						if (err.response?.data.detail) {
							window.location.href = err.response.data.detail;
						} else {
							this.handleError('Error ', err.response?.data?.message, false);
						}
					});
			},

			editSavedPaymentMethods: function() {
				return this.httpClient
					.get('/api/docs/invoice/managePaymentMethods', {
						params: {
							token: this.token,
							redirectUri: location.href,
						},
					})
					.then((res) => {
						document.location.href = res.data;
					})
					.catch((err) => this.handleError(err.response.data.message));
			},

			initiateStripeCheckout: function() {
				this.showInlinePayment();
			},

			showInlinePayment: async function() {
				this.$store.commit('startLoading');
				try {
					const res = await this.httpClient.get('/api/docs/invoice/payment/elements', {
						params: { token: this.token },
					});
					this.$store.commit('stopLoading');

					this.convenienceFee = parseFloat(res.data.convenienceFee) || 0;
					this.paymentIntentId = res.data.paymentIntentId;
					this.baseAmount = this.paymentInfo.invoiceAmount;
					this.paymentIntentHasCardFee = false;

					this.stripe = window.Stripe(this.$store.state.stripeKey, {
						stripeAccount: res.data.stripeAccountId,
					});

					this.stripeElements = this.stripe.elements({
						clientSecret: res.data.clientSecret,
						paymentMethodCreation: 'manual',
						appearance: this.stripeAppearance,
					});

					this.paymentElement = this.stripeElements.create('payment');
					this.showPaymentForm = true;
					await this.$nextTick();
					if (!this.$refs.paymentElement) {
						throw new Error('Payment form is not available');
					}
					this.paymentElement.mount(this.$refs.paymentElement);
				} catch (err) {
					this.$store.commit('stopLoading');
					this.handleError(err.response?.data?.message || err.message || 'Error initializing payment');
				}
			},

			destroyPaymentElement: async function() {
				// If the PI was updated to include the card fee, reset it to base amount
				if (this.paymentIntentHasCardFee && this.paymentIntentId) {
					try {
						await this.httpClient.post('/api/docs/invoice/payment/elements', null, {
							params: {
								token: this.token,
								paymentIntentId: this.paymentIntentId,
								amount: this.baseAmount,
							},
						});
					} catch (e) {
						console.error('Failed to reset payment intent amount', e);
					}
					this.paymentIntentHasCardFee = false;
				}
				if (this.paymentElement) {
					this.paymentElement.destroy();
					this.paymentElement = null;
				}
				this.stripeElements = null;
				this.stripe = null;
				this.showPaymentForm = false;
			},

			submitPayment: async function() {
				this.paymentProcessing = true;
				try {
					const { error: submitError } = await this.stripeElements.submit();
					if (submitError) {
						this.handleError(submitError.message);
						return;
					}

					const { paymentMethod, error: pmError } = await this.stripe.createPaymentMethod({
						elements: this.stripeElements,
					});
					if (pmError) {
						this.handleError(pmError.message);
						return;
					}

					// Determine if this is a card-type payment and apply the convenience fee
					const cardTypes = ['apple_pay', 'google_pay', 'microsoft_pay', 'card'];
					const isCard = cardTypes.includes(paymentMethod.type);

					if (isCard && this.convenienceFee > 0) {
						await this.httpClient.post('/api/docs/invoice/payment/elements', null, {
							params: {
								token: this.token,
								paymentIntentId: this.paymentIntentId,
								amount: this.baseAmount + this.convenienceFee,
							},
						});
						this.paymentIntentHasCardFee = true;
					}

					const { error } = await this.stripe.confirmPayment({
						elements: this.stripeElements,
						redirect: 'if_required',
					});

					if (error) {
						this.handleError(error.message);
					} else {
						this.handleSuccess(
							this.$t('invoices.payment.success') || 'Payment successful!',
							this.paymentInfo.viewOnlineUrl
						);
					}
				} catch (err) {
					this.handleError(err.response?.data?.message || 'Payment error');
				} finally {
					this.paymentProcessing = false;
				}
			},

			initializePayPal: function() {
				let clientId, attributionId;
				if (this.paypalConnection.version === 'STANDARD') {
					console.log('Standard connection detected. ' + this.paypalConnection.clientId);
					clientId = this.paypalConnection.clientId;
					attributionId = null;
				} else {
					console.log('Partner connection detected. ' + this.$store.state.payPalClientId);
					clientId = this.$store.state.payPalClientId;
					attributionId = 'StruxtureTechnologiesInc_SP_PPCP';
				}

				loadScript({
					'client-id': clientId,
					currency: this.paymentInfo.currency,
					'merchant-id': [this.paymentInfo.payPalConnection.merchantId],
					'data-partner-attribution-id': attributionId,
				}).then((paypal) => {
					paypal
						.Buttons({
							createOrder: (data, actions) => {
								return actions.order.create({
									purchase_units: [
										{
											custom_id: this.paymentInfo.invoiceId,
											invoice_id: this.paymentInfo.invoiceNumberFormatted,
											amount: {
												value: this.paymentInfo.invoiceAmount,
											},
										},
									],
								});
							},

							onApprove: (data, actions) => {
								return actions.order.capture().then((orderData) => {
									let status = orderData.status;
									let id = orderData.id;

									if (status === 'COMPLETED') {
										this.$store.commit('startLoading');
										let href = orderData.links.filter((l) => l.rel === 'self')[0].href;
										this.capturePayPalPayment(href)
											.then((res) => {
												this.$store.commit('stopLoading');
												window.location.href = res.data;
											})
											.catch((err) => {
												this.$store.commit('stopLoading');
												this.handleError(err.response.data.message, this.paymentInfo.returnUrl);
											});
									} else {
										this.handleError(
											id + ':' + this.$t('error.order-not-complete') + status,
											this.paymentInfo.returnUrl
										);
									}
								});
							},

							onError: (err) => {
								this.handleError(err.message);
							},
						})
						.render('#paypal-button-container')
						.catch((error) => {
							console.error('failed to render the PayPal Buttons', error);
						});
				});
			},

			getPaymentMethods: function() {
				return this.httpClient
					.get('/api/docs/invoice/paymentInfo', {
						params: {
							token: this.token,
						},
					})
					.then((response) => Promise.resolve(response))
					.catch((error) => Promise.reject(error));
			},

			finalizePaymentIntent: function() {
				return this.httpClient
					.post('/api/docs/invoice/payment/intent', null, {
						params: {
							token: this.token,
							paymentIntent: this.paymentIntent,
						},
					})
					.then((response) => Promise.resolve(response))
					.catch((error) => Promise.reject(error));
			},

			capturePayPalPayment: function(href) {
				return this.httpClient
					.post('/api/docs/invoice/payPalCapture', null, {
						params: {
							token: this.token,
							href: href,
						},
					})
					.then((response) => Promise.resolve(response))
					.catch((error) => Promise.reject(error));
			},


			initializeACHLink: function() {
				this.$store.commit('startLoading');
				return this.httpClient
					.post('/api/docs/invoice/bankAccounts/session', null, {
						params: {
							token: this.token,
							redirectUri: window.location.href,
						},
					})
					.then((res) => {
						window.location.href = res.data.linkUri;
					})
					.catch((error) => console.log(error))
					.finally(() => this.$store.commit('stopLoading'));
			},

			confirmPayment(account) {
				console.log(this.$t('invoices.payment.confirm'));

				let prompt;
				let total = 0;

				if (this.paymentInfo.stripeCardFeeRate && account.type === 'card') {
					let feeLabel = (this.paymentInfo.stripeCardFeeRate * 100).toFixed(2) + '%';
					let feeAmount = this.paymentInfo.stripeCardFeeAmount;
					total = this.paymentInfo.invoiceAmount + feeAmount;
					prompt = `Please confirm your payment of ${this.$formatters.dollars(
						total,
						true,
						true,
						this.paymentInfo.currency
					)}, which includes a ${this.$formatters.dollars(
						feeAmount,
						true,
						true,
						this.paymentInfo.currency
					)} (${feeLabel}) processing fee, from account ending in ${account.last4}.`;
				} else {
					total = this.paymentInfo.invoiceAmount;
					prompt = `Please confirm your payment of ${this.$formatters.dollars(
						total,
						true,
						true,
						this.paymentInfo.currency
					)} from account ending in ${account.last4}.`;
				}

				let binding = {
					severity: 'primary',
					headingText: this.$t('invoices.payment.confirm'),
					bodyText: prompt,
				};

				this.$store.state.globalModalController.openModal(ConfirmModal, binding, false, false).then((res) => {
					if (res) {
						this.$store.commit('startLoading');
						this.httpClient
							.post('/api/docs/invoice/payment', null, {
								params: {
									token: this.token,
									amount: total,
									paymentMethodId: account.id,
								},
							})
							.then((res) => {
								setTimeout(() => {
									window.location.href = res.data;
								}, 2000);
							})
							.catch((err) => {
								this.$store.commit('stopLoading');
								if (err.response.status === 412) {
									this.handle3DSRedirect(err.response.data.message);
								} else {
									this.handleError(err.response.data.message);
								}
							});
					}
				});
			},

			handle3DSRedirect: function(url) {
				window.location.href = url;
			},

			handleError: function(message, returnUrl, doClose = false) {
				let binding = {
					headingText: this.$t('error.connecting-account'),
					bodyText: message,
					hideNo: true,
					yesText: this.$t('global.okay'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, true, false).then(() => {
					if (returnUrl) {
						window.location.href = returnUrl;
					} else if (doClose) {
						window.self.close();
					}
				});
			},

			handleSuccess: function(message, returnUrl) {
				let binding = {
					icon: 'done',
					headingText: 'Success',
					bodyText: message,
					hideNo: true,
					yesText: this.$t('global.okay'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, true, false).then(() => {
					window.location.href = returnUrl;
				});
			},
		},

		computed: {
			effectiveAppearance() {
				return this.$vuetify.theme.dark ? 'dark' : 'light';
			},

			branding() {
				return resolveDocumentBranding(this.capturedBranding, this.effectiveAppearance);
			},

			brandClasses() {
				return this.branding ? ['portal-brand-kit', ...formBrandClasses(this.branding)] : [];
			},

			brandStyle() {
				return this.branding ? {
					...portalBrandStyle(this.branding),
					'--v-primary-base': this.branding.buttons.fill,
				} : {
					'--brand-button-solidText': readableTextColor(this.$vuetify.theme.currentTheme.primary),
				};
			},

			stripeAppearance() {
				if (!this.branding) return { theme: 'stripe' };
				const style = formBrandStyle(this.branding);
				return {
					theme: this.effectiveAppearance === 'dark' ? 'night' : 'stripe',
					variables: {
						colorPrimary: this.branding.colors.primary,
						colorBackground: this.branding.colors.surface,
						colorText: this.branding.surfaceText,
						fontFamily: this.branding.typography.bodyFamily,
						fontSizeBase: `${this.branding.typography.bodySize}px`,
						borderRadius: `${this.branding.fields.radius}px`,
					},
					rules: {
						'.Input': {
							backgroundColor: style['--brand-field-fill'],
							color: this.branding.typography.roles.answer.color,
							border: `${this.branding.fields.borderWidth}px solid ${this.branding.fields.border}`,
						},
						'.Input:focus': { borderColor: this.branding.states.focusBorder },
					},
				};
			},
			paymentOptionCount: function() {
				let count = 0;

				if (this.achEnabled || this.paymentMethods.length) {
					count++;
				}

				if (this.cardEnabled) {
					count++;
				}

				if (this.payPalEnabled) {
					count++;
				}

				return count;
			},

			achEnabled: function() {
				if (this.paymentInfo && this.paymentInfo.achPaymentsEnabled) {
					return true;
				} else {
					return false;
				}
			},

			cardEnabled: function() {
				if (this.paymentInfo && this.paymentInfo.cardPaymentsEnabled) {
					return true;
				} else {
					return false;
				}
			},

			payPalEnabled: function() {
				if (this.paymentInfo && this.paymentInfo.payPalPaymentsEnabled) {
					return true;
				} else {
					return false;
				}
			},

			paymentMethods: function() {
				if (this.paymentInfo.paymentMethods) {
					return this.paymentInfo.paymentMethods.filter((a) => a.status === 'verified');
				} else {
					return [];
				}
			},

			logo: function() {
				if (this.paymentInfo) {
					return this.branding
						? formLogo(this.branding, this.paymentInfo.accountLogo, this.branding.colors.background)
						: this.paymentInfo.accountLogo;
				} else {
					return null;
				}
			},
		},

		watch: {
			'$vuetify.theme.dark'() {
				if (this.stripeElements) this.stripeElements.update({ appearance: this.stripeAppearance });
			},
		},
	};
</script>

<style scoped lang="scss">
	.payment-page {
		width: 100%;
		min-height: 100%;
		overflow: auto;
		background:
			radial-gradient(circle at 12% 0%, color-mix(in srgb, var(--v-primary-base) 8%, transparent), transparent 30rem),
			var(--portal-canvas);
		color: var(--portal-text);
		text-align: left;
	}

	.payment-page.portal-brand-kit {
		font-family: var(--brand-body-family), sans-serif;
		font-size: var(--brand-body-size);
		font-weight: var(--brand-body-weight);
	}

	.payment-page.portal-brand-kit h1 {
		font-family: var(--brand-h1-family), sans-serif;
		font-size: var(--brand-h1-size);
		font-weight: var(--brand-h1-weight);
		color: var(--brand-h1-color);
	}

	.payment-page.portal-brand-kit h2 {
		font-family: var(--brand-h2-family), sans-serif;
		font-size: var(--brand-h2-size);
		font-weight: var(--brand-h2-weight);
		color: var(--brand-h2-color);
	}

	.payment-shell {
		width: min(1120px, 100%);
		margin: 0 auto;
		padding: 32px 28px 56px;
	}

	.payment-loading {
		min-height: 100vh;
		display: grid;
		place-content: center;
		justify-items: center;
		gap: 16px;
		color: var(--portal-muted);
	}

	.payment-loading p {
		margin: 0;
		font-size: 15px;
		font-weight: 560;
	}

	.payment-brand {
		min-height: 58px;
		display: flex;
		align-items: center;
		margin-bottom: 22px;
	}

	.payment-brand img {
		display: block;
		width: auto;
		max-width: min(220px, 62vw);
		max-height: 58px;
		object-fit: contain;
		object-position: left center;
	}

	.invoice-summary {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		gap: 24px 36px;
		padding: 28px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 22px);
		background: var(--portal-surface);
		box-shadow: 0 18px 50px rgba(16, 17, 15, 0.06);
	}

	.summary-heading {
		align-self: center;
		text-align: left;
	}

	.invoice-kicker {
		display: block;
		margin-bottom: 8px;
		color: var(--v-primary-base);
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
	}

	.summary-heading h1 {
		max-width: 620px;
		margin: 0;
		color: var(--portal-text);
		font-size: clamp(24px, 4vw, 36px);
		font-weight: 650;
		letter-spacing: -0.035em;
		line-height: 1.12;
	}

	.amount-summary {
		min-width: 210px;
		align-self: stretch;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 20px 22px;
		border-radius: var(--brand-surface-radius, 16px);
		background: color-mix(in srgb, var(--v-primary-base) 8%, var(--portal-surface));
		text-align: right;
	}

	.amount-summary span {
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 650;
	}

	.amount-summary strong {
		margin-top: 5px;
		color: var(--portal-text);
		font-size: clamp(25px, 4vw, 34px);
		font-weight: 680;
		letter-spacing: -0.035em;
		line-height: 1;
	}

	.invoice-facts {
		grid-column: 1 / -1;
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 0;
		margin: 0;
		padding-top: 22px;
		border-top: 1px solid var(--portal-border);
	}

	.invoice-facts > div {
		min-width: 0;
		padding: 0 22px;
		border-right: 1px solid var(--portal-border);
		text-align: left;
	}

	.invoice-facts > div:first-child {
		padding-left: 0;
	}

	.invoice-facts > div:last-child {
		padding-right: 0;
		border-right: 0;
	}

	.invoice-facts dt {
		margin-bottom: 5px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.invoice-facts dd {
		margin: 0;
		overflow: hidden;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.payment-fee-notice {
		margin: 18px 0 0 !important;
		border-radius: var(--brand-surface-radius, 12px);
		font-size: 13px;
	}

	.payment-option-grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		align-items: stretch;
		gap: 16px;
		margin-top: 18px;
	}

	.payment-option-grid.option-count-1 {
		grid-template-columns: minmax(0, 720px);
		justify-content: center;
	}

	.payment-option-grid.option-count-2 {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.payment-option-card,
	.payment-form-card {
		min-width: 0;
		padding: 22px;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		box-shadow: 0 1px 2px rgba(16, 17, 15, 0.03);
	}

	.payment-option-card {
		display: flex;
		flex-direction: column;
	}

	.option-heading {
		min-height: 40px;
		display: flex;
		align-items: center;
		gap: 11px;
		margin-bottom: 20px;
	}

	.option-heading h2 {
		min-width: 0;
		flex: 1;
		margin: 0;
		color: var(--portal-text);
		font-size: 15px;
		font-weight: 650;
		letter-spacing: -0.01em;
		line-height: 1.3;
	}

	.option-icon,
	.saved-method-icon {
		display: grid;
		place-items: center;
		border-radius: 11px;
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.option-icon {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
	}

	.paypal-icon {
		color: #173ea5;
		font-size: 17px;
		font-style: italic;
		font-weight: 800;
	}

	.option-action {
		width: 36px;
		height: 36px;
		flex: 0 0 36px;
		display: grid;
		place-items: center;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
		cursor: pointer;
	}

	.option-action:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.saved-method-list {
		display: grid;
		gap: 8px;
	}

	.saved-method {
		width: 100%;
		min-width: 0;
		min-height: 58px;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 9px 11px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 12px);
		background: var(--portal-surface);
		color: var(--portal-text);
		cursor: pointer;
		text-align: left;
		transition: border-color 150ms ease, background-color 150ms ease, transform 150ms ease;
	}

	.saved-method:hover {
		border-color: color-mix(in srgb, var(--v-primary-base) 34%, var(--portal-border));
		background: color-mix(in srgb, var(--v-primary-base) 3%, var(--portal-surface));
		transform: translateY(-1px);
	}

	.saved-method-icon {
		width: 36px;
		height: 36px;
		flex: 0 0 36px;
	}

	.saved-method-details {
		min-width: 0;
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	.saved-method-details strong,
	.saved-method-details span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.saved-method-details strong {
		font-size: 13px;
		font-weight: 650;
	}

	.saved-method-details span {
		color: var(--portal-muted);
		font-size: 12px;
	}

	.saved-method-action {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 4px;
		color: var(--v-primary-base);
		font-size: 11px;
		font-weight: 650;
		white-space: nowrap;
	}

	.empty-methods {
		margin: 4px 0 18px;
		color: var(--portal-muted);
		font-size: 13px;
		line-height: 1.5;
	}

	.secondary-payment-action,
	.primary-payment-action,
	.back-action {
		display: flex;
		align-items: center;
		justify-content: center;
		border: 0;
		cursor: pointer;
		font-family: var(--primary-font);
		font-weight: 650;
	}

	.secondary-payment-action {
		width: 100%;
		min-height: 42px;
		gap: 7px;
		margin-top: 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 11px);
		background: var(--portal-surface);
		color: var(--v-primary-base);
		font-size: 13px;
	}

	.secondary-payment-action:hover {
		background: color-mix(in srgb, var(--v-primary-base) 4%, var(--portal-surface));
	}

	.card-brand-row {
		min-height: 54px;
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.wallet-options {
		display: flex;
		flex-wrap: wrap;
		gap: 7px;
		margin: 9px 0 20px;
	}

	.wallet-options span {
		padding: 6px 9px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.primary-payment-action {
		width: 100%;
		min-height: 46px;
		gap: 8px;
		margin-top: auto;
		padding: 0 18px;
		border-radius: var(--brand-button-radius, 12px);
		background: var(--brand-button-fill, var(--v-primary-base));
		color: var(--brand-button-solidText, var(--v-white-base));
		font-size: 14px;
		box-shadow: 0 8px 18px color-mix(in srgb, var(--v-primary-base) 22%, transparent);
		transition: filter 150ms ease, transform 150ms ease;
	}

	.primary-payment-action:hover {
		filter: brightness(0.96);
		transform: translateY(-1px);
	}

	.payment-provider {
		display: flex;
		align-items: center;
		gap: 7px;
		margin-top: 16px;
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 560;
	}

	.payment-provider img {
		width: 38px;
		height: auto;
	}

	.paypal-button-container {
		min-height: 150px;
		margin-top: 2px;
	}

	.payment-form-card {
		width: min(720px, 100%);
		margin: 18px auto 0;
		padding: 26px;
	}

	.payment-form-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		margin-bottom: 24px;
		padding-bottom: 18px;
		border-bottom: 1px solid var(--portal-border);
	}

	.back-action {
		gap: 7px;
		padding: 8px 10px;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
		font-size: 13px;
	}

	.back-action:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.payment-provider-top {
		margin: 0;
	}

	.stripe-submit {
		margin-top: 22px !important;
		background-color: var(--brand-button-fill, var(--v-primary-base)) !important;
		letter-spacing: 0 !important;
		text-transform: none !important;
	}

	.stripe-submit ::v-deep .v-btn__content {
		color: inherit;
		font-size: 14px;
		font-weight: 650;
	}

	button:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	@media (max-width: 699px) {
		.payment-shell {
			padding: 20px 14px 36px;
		}

		.payment-brand {
			min-height: 48px;
			margin-bottom: 16px;
		}

		.payment-brand img {
			max-height: 48px;
		}

		.invoice-summary {
			grid-template-columns: 1fr;
			gap: 20px;
			padding: 20px;
			border-radius: var(--brand-surface-radius, 17px);
		}

		.amount-summary {
			min-width: 0;
			align-items: flex-start;
			padding: 16px;
			text-align: left;
		}

		.invoice-facts {
			grid-template-columns: 1fr;
			gap: 12px;
			padding-top: 18px;
		}

		.invoice-facts > div,
		.invoice-facts > div:first-child,
		.invoice-facts > div:last-child {
			display: flex;
			align-items: baseline;
			justify-content: space-between;
			gap: 18px;
			padding: 0;
			border: 0;
		}

		.invoice-facts dt {
			margin: 0;
		}

		.payment-option-card,
		.payment-form-card {
			padding: 18px;
			border-radius: var(--brand-surface-radius, 14px);
		}

		.payment-option-grid,
		.payment-option-grid.option-count-1,
		.payment-option-grid.option-count-2 {
			grid-template-columns: minmax(0, 1fr);
		}

		.saved-method {
			align-items: flex-start;
		}

		.saved-method-action {
			font-size: 0;
		}

		.saved-method-action svg {
			margin-top: 9px;
		}

		.payment-form-heading {
			align-items: flex-start;
		}

		.payment-provider-top {
			flex-direction: column;
			align-items: flex-end;
			gap: 2px;
		}
	}

	@media (min-width: 700px) and (max-width: 940px) {
		.saved-method-action {
			font-size: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.saved-method,
		.primary-payment-action {
			transition: none;
		}
	}
</style>
